import { useRef, useState } from "react";
import { Loader2, ShieldCheck, Sparkles, Upload } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const FIELDS = [["name", "Name"], ["mobile", "Mobile"], ["email", "Email"],
                ["company", "Company"], ["city", "City"], ["requirement", "Requirement"]];

export default function ContactImport() {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState("Bulk import");

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const body = new FormData();
      Array.from(files).forEach((f) => body.append("files", f));
      const { data } = await api.post("/contact-import/parse", body, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 });
      setResult(data);
      setRows(data.new_contacts);
      toast.success(`${data.counts.new} new contact(s) found, ${data.counts.duplicates} already in CRM`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
      if (camRef.current) camRef.current.value = "";
    }
  };

  const setCell = (i, key, value) =>
    setRows((r) => r.map((row, x) => (x === i ? { ...row, [key]: value } : row)));

  const commit = async () => {
    const contacts = rows.filter((r) => r.mobile || r.email);
    if (!contacts.length) return toast.error("Nothing to save");
    setSaving(true);
    try {
      const { data } = await api.post("/contact-import/commit", { contacts, source });
      toast.success(`${data.created_count} lead(s) added`);
      if (data.skipped?.length) toast.info(`${data.skipped.length} skipped (already in CRM)`);
      setRows([]);
      setResult(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        testId="contact-import-header"
        title="Import contacts &amp; leads"
        subtitle="Photos, WhatsApp screenshots, call logs, PDFs, Word, Excel or CSV — up to 10,000 contacts at a time"
        actions={
          <>
            <Input className="w-44" value={source} data-testid="import-source-input"
              onChange={(e) => setSource(e.target.value)} placeholder="Source label" />
            <Button onClick={() => camRef.current?.click()} variant="outline" disabled={busy}
              data-testid="import-camera-button">Snap with camera</Button>
            <Button onClick={() => fileRef.current?.click()} disabled={busy}
              data-testid="import-upload-button" className="gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {busy ? "Reading…" : "Choose files"}
            </Button>
          </>
        }
      />
      <input ref={camRef} type="file" accept="image/*" capture="environment" multiple hidden
        data-testid="import-camera-input" onChange={(e) => upload(e.target.files)} />
      <input ref={fileRef} type="file" multiple hidden data-testid="import-file-input"
        accept="image/*,application/pdf,.docx,.csv,.xlsx,.xls,.txt,.vcf"
        onChange={(e) => upload(e.target.files)} />

      <div className="p-4 sm:p-6">
        {!result ? (
          <div className="surface p-10 text-center" data-testid="import-empty">
            <Sparkles className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Your files are read on this server first — free of AI credits. Only the lines we
              cannot understand go to the AI, in small batches. Numbers already in your CRM are
              never added twice.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-1.5" data-testid="import-counts">
              <Badge variant="secondary">{result.counts.new} new</Badge>
              <Badge variant="outline">{result.counts.duplicates} already in CRM</Badge>
              <Badge variant="outline">{result.counts.read_locally} read free (no AI)</Badge>
              <Badge variant="outline">{result.counts.read_by_ai} read by AI</Badge>
              <Badge variant="outline">{result.ai_batches_used} AI batch(es) used</Badge>
            </div>

            <Tabs defaultValue="new">
              <TabsList data-testid="import-tabs">
                <TabsTrigger value="new" data-testid="tab-new-contacts">
                  New ({rows.length})
                </TabsTrigger>
                <TabsTrigger value="dupes" data-testid="tab-duplicates">
                  Duplicates ({result.duplicates.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="new">
                {rows.length === 0 ? (
                  <p className="py-10 text-center text-sm text-muted-foreground">
                    No new contacts in these files.
                  </p>
                ) : (
                  <>
                    <div className="surface max-h-[60vh] overflow-auto" data-testid="import-grid">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {FIELDS.map(([, l]) => <TableHead key={l}>{l}</TableHead>)}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map((r, i) => (
                            <TableRow key={i} data-testid={`import-row-${i}`}>
                              {FIELDS.map(([k]) => (
                                <TableCell key={k} className="p-1">
                                  <Input className="h-8 text-xs" value={r[k] || ""}
                                    data-testid={`import-${k}-${i}`}
                                    onChange={(e) => setCell(i, k, e.target.value)} />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button onClick={commit} disabled={saving} data-testid="import-save-button">
                        {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                        Save {rows.length} contact(s) as leads
                      </Button>
                    </div>
                  </>
                )}
              </TabsContent>

              <TabsContent value="dupes">
                <div className="surface overflow-x-auto" data-testid="import-duplicates">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>Already saved as</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.duplicates.map((d, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs">{d.name || "—"}</TableCell>
                          <TableCell className="font-mono-num text-xs">{d.mobile}</TableCell>
                          <TableCell className="text-xs">{d.existing_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}

        <p className="mt-6 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" />
          Imported contacts follow the same security rules — nobody can export the lead or
          customer list without the Owner's one-time code.
        </p>
      </div>
    </div>
  );
}
